import "maplibre-gl/dist/maplibre-gl.css";

import { Locate, Loader2, Maximize, Minus, Plus } from "lucide-react";
import MapLibreGL from "maplibre-gl";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { MAPCN_MAP_STYLES } from "@/lib/maps/mapcn";
import { cn } from "@/lib/utils";

type Theme = "light" | "dark";

export type MapViewport = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

type MapStyleOption = string | MapLibreGL.StyleSpecification;

export type MapRef = MapLibreGL.Map;

type MapProps = {
  children?: ReactNode;
  className?: string;
  theme?: Theme;
  styles?: {
    light?: MapStyleOption;
    dark?: MapStyleOption;
  };
  viewport?: Partial<MapViewport>;
  onViewportChange?: (viewport: MapViewport) => void;
  loading?: boolean;
  onLoadError?: (error: Error) => void;
} & Omit<MapLibreGL.MapOptions, "container" | "style">;

type MapContextValue = {
  map: MapLibreGL.Map | null;
  isLoaded: boolean;
};

const MapContext = createContext<MapContextValue | null>(null);

const getDocumentTheme = (): Theme | null => {
  if (typeof document === "undefined") {
    return null;
  }
  if (document.documentElement.classList.contains("dark")) {
    return "dark";
  }
  if (document.documentElement.classList.contains("light")) {
    return "light";
  }
  return null;
};

const getSystemTheme = (): Theme => {
  if (typeof window === "undefined") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getViewport = (map: MapLibreGL.Map): MapViewport => {
  const center = map.getCenter();
  return {
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
};

const useResolvedTheme = (theme?: Theme) => {
  const [detectedTheme, setDetectedTheme] = useState<Theme>(
    () => getDocumentTheme() ?? getSystemTheme(),
  );

  useEffect(() => {
    if (theme) {
      return;
    }

    const observer = new MutationObserver(() => {
      const documentTheme = getDocumentTheme();
      if (documentTheme) {
        setDetectedTheme(documentTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemChange = (event: MediaQueryListEvent) => {
      if (!getDocumentTheme()) {
        setDetectedTheme(event.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleSystemChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleSystemChange);
    };
  }, [theme]);

  return theme ?? detectedTheme;
};

export const useMap = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error("useMap must be used within a Map component");
  }
  return context;
};

function DefaultLoader() {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-xs">
      <div className="flex gap-1">
        <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/60" />
        <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:150ms]" />
        <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:300ms]" />
      </div>
    </div>
  );
}

export const Map = forwardRef<MapRef, MapProps>(function Map(
  {
    children,
    className,
    theme,
    styles,
    viewport,
    onViewportChange,
    loading = false,
    onLoadError,
    ...props
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mapInstance, setMapInstance] = useState<MapLibreGL.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const resolvedTheme = useResolvedTheme(theme);
  const currentStyleRef = useRef<MapStyleOption | null>(null);
  const initialStyleRef = useRef<MapStyleOption | null>(null);
  const onViewportChangeRef = useRef(onViewportChange);
  const onLoadErrorRef = useRef(onLoadError);
  const initialPropsRef = useRef(props);
  const initialViewportRef = useRef(viewport);

  onViewportChangeRef.current = onViewportChange;
  onLoadErrorRef.current = onLoadError;

  const mapStyles = useMemo(
    () => ({
      light: styles?.light ?? MAPCN_MAP_STYLES.light,
      dark: styles?.dark ?? MAPCN_MAP_STYLES.dark,
    }),
    [styles],
  );

  useImperativeHandle(ref, () => mapInstance as MapLibreGL.Map, [mapInstance]);
  if (!initialStyleRef.current) {
    initialStyleRef.current = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
  }

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const initialStyle = initialStyleRef.current;
    if (!initialStyle) {
      return;
    }
    currentStyleRef.current = initialStyle;

    const map = new MapLibreGL.Map({
      container: containerRef.current,
      style: initialStyle,
      renderWorldCopies: false,
      attributionControl: { compact: true },
      ...initialPropsRef.current,
      ...initialViewportRef.current,
    });

    const handleLoad = () => setIsLoaded(true);
    const handleMove = () => onViewportChangeRef.current?.(getViewport(map));
    const handleError = (event: { error?: { message?: string } }) => {
      const message = event?.error?.message ?? "Map failed to load";
      onLoadErrorRef.current?.(new Error(message));
    };

    map.on("load", handleLoad);
    map.on("move", handleMove);
    map.on("error", handleError);
    setMapInstance(map);

    return () => {
      map.off("load", handleLoad);
      map.off("move", handleMove);
      map.off("error", handleError);
      map.remove();
      setMapInstance(null);
      setIsLoaded(false);
    };
  }, []);

  useEffect(() => {
    if (!(mapInstance && viewport)) {
      return;
    }

    const current = getViewport(mapInstance);
    const next = {
      center: viewport.center ?? current.center,
      zoom: viewport.zoom ?? current.zoom,
      bearing: viewport.bearing ?? current.bearing,
      pitch: viewport.pitch ?? current.pitch,
    };

    if (
      next.center[0] === current.center[0] &&
      next.center[1] === current.center[1] &&
      next.zoom === current.zoom &&
      next.bearing === current.bearing &&
      next.pitch === current.pitch
    ) {
      return;
    }

    mapInstance.jumpTo(next);
  }, [mapInstance, viewport]);

  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    const nextStyle = resolvedTheme === "dark" ? mapStyles.dark : mapStyles.light;
    if (currentStyleRef.current === nextStyle) {
      return;
    }

    currentStyleRef.current = nextStyle;
    mapInstance.setStyle(nextStyle, { diff: true });
  }, [mapInstance, mapStyles.dark, mapStyles.light, resolvedTheme]);

  const contextValue = useMemo(() => ({ map: mapInstance, isLoaded }), [mapInstance, isLoaded]);

  return (
    <MapContext.Provider value={contextValue}>
      <div ref={containerRef} className={cn("relative h-full w-full", className)}>
        {!isLoaded || loading ? <DefaultLoader /> : null}
        {mapInstance ? children : null}
      </div>
    </MapContext.Provider>
  );
});

type MapMarkerProps = {
  longitude: number;
  latitude: number;
  children: ReactNode;
  onDragStart?: (lngLat: { lng: number; lat: number }) => void;
  onDrag?: (lngLat: { lng: number; lat: number }) => void;
  onDragEnd?: (lngLat: { lng: number; lat: number }) => void;
} & Omit<MapLibreGL.MarkerOptions, "element">;

type MarkerContextValue = {
  marker: MapLibreGL.Marker;
};

const MarkerContext = createContext<MarkerContextValue | null>(null);

const useMarkerContext = () => {
  const context = useContext(MarkerContext);
  if (!context) {
    throw new Error("Marker components must be used within MapMarker");
  }
  return context;
};

export function MapMarker({
  longitude,
  latitude,
  children,
  onDragStart,
  onDrag,
  onDragEnd,
  draggable = false,
  ...markerOptions
}: MapMarkerProps) {
  const { map } = useMap();
  const markerRef = useRef<MapLibreGL.Marker | null>(null);
  const callbacksRef = useRef({ onDragStart, onDrag, onDragEnd });
  callbacksRef.current = { onDragStart, onDrag, onDragEnd };

  if (!markerRef.current) {
    markerRef.current = new MapLibreGL.Marker({
      ...markerOptions,
      element: document.createElement("div"),
      draggable,
    }).setLngLat([longitude, latitude]);
  }

  const marker = markerRef.current;

  useEffect(() => {
    if (!map) {
      return;
    }

    const handleDragStart = () => {
      const lngLat = marker.getLngLat();
      callbacksRef.current.onDragStart?.({ lng: lngLat.lng, lat: lngLat.lat });
    };
    const handleDrag = () => {
      const lngLat = marker.getLngLat();
      callbacksRef.current.onDrag?.({ lng: lngLat.lng, lat: lngLat.lat });
    };
    const handleDragEnd = () => {
      const lngLat = marker.getLngLat();
      callbacksRef.current.onDragEnd?.({ lng: lngLat.lng, lat: lngLat.lat });
    };

    marker.on("dragstart", handleDragStart);
    marker.on("drag", handleDrag);
    marker.on("dragend", handleDragEnd);
    marker.addTo(map);

    return () => {
      marker.off("dragstart", handleDragStart);
      marker.off("drag", handleDrag);
      marker.off("dragend", handleDragEnd);
      marker.remove();
    };
  }, [map, marker]);

  if (marker.getLngLat().lng !== longitude || marker.getLngLat().lat !== latitude) {
    marker.setLngLat([longitude, latitude]);
  }
  if (marker.isDraggable() !== draggable) {
    marker.setDraggable(draggable);
  }

  return <MarkerContext.Provider value={{ marker }}>{children}</MarkerContext.Provider>;
}

type MarkerContentProps = {
  children?: ReactNode;
  className?: string;
};

export function MarkerContent({ children, className }: MarkerContentProps) {
  const { marker } = useMarkerContext();

  return createPortal(
    <div className={cn("relative cursor-pointer", className)}>
      {children ?? (
        <div className="size-4 rounded-full border-2 border-white bg-primary shadow-lg" />
      )}
    </div>,
    marker.getElement(),
  );
}

type MapControlsProps = {
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  showZoom?: boolean;
  showLocate?: boolean;
  showFullscreen?: boolean;
  className?: string;
  onLocate?: (coords: { longitude: number; latitude: number }) => void;
  onLocateError?: (status: "denied" | "unavailable" | "timeout" | "unsupported") => void;
};

const positionClasses = {
  "top-left": "top-2 left-2",
  "top-right": "top-2 right-2",
  "bottom-left": "bottom-2 left-2",
  "bottom-right": "right-2 bottom-8",
};

function ControlGroup({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-border bg-background shadow-sm [&>button:not(:last-child)]:border-b [&>button:not(:last-child)]:border-border">
      {children}
    </div>
  );
}

function ControlButton({
  onClick,
  label,
  children,
  disabled = false,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex size-8 items-center justify-center transition-all hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function MapControls({
  position = "bottom-right",
  showZoom = true,
  showLocate = false,
  showFullscreen = false,
  className,
  onLocate,
  onLocateError,
}: MapControlsProps) {
  const { map } = useMap();
  const [waitingForLocation, setWaitingForLocation] = useState(false);

  const handleZoomIn = useCallback(() => {
    map?.zoomTo(map.getZoom() + 1, { duration: 300 });
  }, [map]);

  const handleZoomOut = useCallback(() => {
    map?.zoomTo(map.getZoom() - 1, { duration: 300 });
  }, [map]);

  const handleLocate = useCallback(() => {
    if (!(map && "geolocation" in navigator)) {
      onLocateError?.("unsupported");
      return;
    }

    setWaitingForLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
        };
        map.flyTo({
          center: [coords.longitude, coords.latitude],
          zoom: 16,
          duration: 900,
        });
        onLocate?.(coords);
        setWaitingForLocation(false);
      },
      (err) => {
        setWaitingForLocation(false);
        if (err.code === 1) {
          onLocateError?.("denied");
        } else if (err.code === 3) {
          onLocateError?.("timeout");
        } else {
          onLocateError?.("unavailable");
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
    );
  }, [map, onLocate, onLocateError]);

  const handleFullscreen = useCallback(() => {
    const container = map?.getContainer();
    if (!container) {
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    void container.requestFullscreen();
  }, [map]);

  return (
    <div
      className={cn("absolute z-10 flex flex-col gap-1.5", positionClasses[position], className)}
    >
      {showZoom ? (
        <ControlGroup>
          <ControlButton label="Acercar" onClick={handleZoomIn}>
            <Plus className="size-4" />
          </ControlButton>
          <ControlButton label="Alejar" onClick={handleZoomOut}>
            <Minus className="size-4" />
          </ControlButton>
        </ControlGroup>
      ) : null}
      {showLocate ? (
        <ControlGroup>
          <ControlButton
            label="Usar mi ubicacion actual"
            onClick={handleLocate}
            disabled={waitingForLocation}
          >
            {waitingForLocation ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Locate className="size-4" />
            )}
          </ControlButton>
        </ControlGroup>
      ) : null}
      {showFullscreen ? (
        <ControlGroup>
          <ControlButton label="Pantalla completa" onClick={handleFullscreen}>
            <Maximize className="size-4" />
          </ControlButton>
        </ControlGroup>
      ) : null}
    </div>
  );
}
